import React, { useState, useEffect } from 'react';
import { sourcesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { BookUser, Plus, Search, Shield, Building2, Mail, Phone } from 'lucide-react';

export default function AdminSources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', organization: '', role: '', email: '', phone: '', expertise: '', notes: '', is_confidential: false });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await sourcesAPI.list({ search: search || undefined });
      setSources(res.data.sources || []);
    } catch { toast.error('Failed to load sources'); }
    finally { setLoading(false); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await sourcesAPI.create({ ...form, expertise: form.expertise ? form.expertise.split(',').map(s => s.trim()) : [] });
      toast.success('Source added');
      setShowCreate(false);
      setForm({ name: '', organization: '', role: '', email: '', phone: '', expertise: '', notes: '', is_confidential: false });
      load();
    } catch { toast.error('Failed to add source'); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Source & Contact Database</h1>
          <p className="text-sm text-[#5B616E] mt-1">Manage journalist sources and expert contacts</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#1E5A8A] hover:bg-[#1E5A8A]/90">
          <Plus className="w-4 h-4 mr-2" /> Add Source
        </Button>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B616E]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search sources..." className="w-full pl-10 pr-4 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
        </div>
        <Button type="submit" variant="outline">Search</Button>
      </form>

      {showCreate && (
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-6">
          <h2 className="text-lg font-semibold mb-4">Add Source Contact</h2>
          <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Name *</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Organization</label>
              <input value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Role/Title</label>
              <input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Email</label>
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email" className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Phone</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Expertise (comma-separated)</label>
              <input value={form.expertise} onChange={e => setForm({ ...form, expertise: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" placeholder="politics, economics, law" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-[#5B616E]">Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_confidential} onChange={e => setForm({ ...form, is_confidential: e.target.checked })} />
                <Shield className="w-4 h-4 text-[#B3261E]" /> Confidential Source
              </label>
            </div>
            <div className="md:col-span-3 flex gap-2">
              <Button type="submit" className="bg-[#1E5A8A]">Add Source</Button>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F0E8] border-b border-[#E5DFD5]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Name</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Organization</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Contact</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Expertise</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5DFD5]">
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#5B616E]">Loading...</td></tr>
              ) : sources.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-[#5B616E]">No sources found.</td></tr>
              ) : sources.map(s => (
                <tr key={s.id} className="hover:bg-[#FBF7EF]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <BookUser className="w-4 h-4 text-[#5B616E]" />
                      <div>
                        <span className="font-medium text-[#1A1A1A]">{s.name}</span>
                        {s.role && <p className="text-xs text-[#5B616E]">{s.role}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">{s.organization || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1 text-xs text-[#5B616E]">
                      {s.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{s.email}</span>}
                      {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{s.phone}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(s.expertise || []).map((e, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]">{e}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {s.is_confidential ? (
                      <Badge className="bg-red-100 text-red-700"><Shield className="w-3 h-3 mr-1" />Confidential</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700">Open</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
